/**
 * A color.
 */
export interface RgbColor {
  /**
   * The red component of the color in the range 0-255.
   */
  readonly r: number;

  /**
   * The green component of the color in the range 0-255.
   */
  readonly g: number;

  /**
   * The blue component of the color in the range 0-255.
   */
  readonly b: number;

  /**
   * The alpha component of the color in the range 0.0-1.0.
   */
  readonly alpha: number;
}

/**
 * An HSL color.
 */
export interface HslColor {
  /**
   * The hue component of the color in the range 0.0-1.0.
   */
  readonly h: number;

  /**
   * The saturation component of the color in the range 0.0-1.0.
   */
  readonly s: number;

  /**
   * The lightness component of the color in the range 0.0-1.0.
   */
  readonly l: number;

  /**
   * The alpha component of the color in the range 0.0-1.0.
   */
  readonly alpha: number;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function convertByteToFloat(value: number): number {
  return clamp(value, 0, 255);
}

/**
 * Converts a color to its CSS RGBA representation.
 * @param color The color to convert.
 * @returns The converted color.
 */
export function toString(color: RgbColor): string {
  const { r, g, b, alpha = 255 } = color;
  return `rgba(${convertByteToFloat(r)}, ${convertByteToFloat(g)}, ${convertByteToFloat(b)}, ${convertByteToFloat(alpha)})`;
}

/**
 * Converts a color from its CSS representation.
 * @param color The CSS color string to convert.
 * @returns The converted color.
 */
export function fromString(color: string): RgbColor {
  const rgbRegex = /^rgba?\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})(?:,\s*([\d.]+))?\)$/i;
  const hexRegex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

  const rgbMatch = color.match(rgbRegex);
  const hexMatch = color.match(hexRegex);

  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    let alpha: number | undefined;

    if (rgbMatch[4] !== undefined) {
      alpha = parseFloat(rgbMatch[4]);
    }

    return {
      r: clamp(r, 0, 255),
      g: clamp(g, 0, 255),
      b: clamp(b, 0, 255),
      alpha: alpha !== undefined ? clamp(alpha, 0, 1) : 1,
    };
  } else if (hexMatch) {
    const hexValue = hexMatch[1];

    const r = parseInt(hexValue.substring(0, 2), 16);
    const g = parseInt(hexValue.substring(2, 4), 16);
    const b = parseInt(hexValue.substring(4, 6), 16);

    return { r, g, b, alpha: 1 };
  } else {
    throw new Error("Invalid CSS color: ${color}");
  }
}

/**
 * Converts an RGB color to its HSL representation.
 * @param color The RGB color to convert to HSL.
 * @returns The HSL color.
 */
export function toHsl(color: RgbColor): HslColor {
  const r = clamp(color.r, 0, 255) / 255;
  const g = clamp(color.g, 0, 255) / 255;
  const b = clamp(color.b, 0, 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h: number;
  let s: number;
  const l = (max + min) / 2;

  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;

    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      default:
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }


    h = h! / 6; // TS gives an error here, but it definitely is set.
  }

  return { h, s, l, alpha: clamp(color.alpha, 0, 1) };
}

/**
 * Converts an HSL color to its RGB representation.
 * @param color The HSL color to convert to RGB.
 * @returns The RGB color.
 */
export function toRgb(color: HslColor): RgbColor {
  const h = clamp(color.h, 0, 1);
  const s = clamp(color.s, 0, 1);
  const l = clamp(color.l, 0, 1);

  function hueToRgb(p: number, q: number, t: number): number {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = 1;
    g = 1;
    b = 1;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hueToRgb(p, q, h + 1 / 3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    alpha: clamp(color.alpha, 0, 1),
  };
}

/**
 * Changes the hue of a color by a given degree.
 * @param color The color whose hue to change.
 * @param degree The number between 0 and 360 indicating the degrees to shift the color by.
 * @returns The new color.
 */
export function changeHue(color: RgbColor, degree: number): RgbColor {
  const { h, s, l, alpha } = toHsl(color);

  const hue = (h + degree / 360) % 1;

  return toRgb({ h: hue, s, l, alpha });
}

/**
 * Lightens the color by the specified amount.
 * @param color The color to lighten.
 * @param percentage The percentage to lighten the color in the range 0.0-1.0
 * @returns The new color.
 */
export function lighten(color: RgbColor, percentage: number): RgbColor {
  percentage = clamp(percentage, 0, 1);

  const { h, s, l, alpha } = toHsl(color);

  const lightness = Math.min(l + percentage, 1); // Question, do we want this to be an absolute increase or a relative one...

  return toRgb({ h, s, l: lightness, alpha });
}

/**
 * Darkens the color by the specified amount.
 * @param color The color to darken.
 * @param percentage The percentage to darken the color in the range 0.0-1.0
 * @returns The new color.
 */
export function darken(color: RgbColor, percentage: number): RgbColor {
  percentage = clamp(percentage, 0, 1);

  const { h, s, l, alpha } = toHsl(color);

  const lightness = Math.max(l - percentage, 0); // Question, do we want this to be an absolute decrease or a relative one...

  return toRgb({ h, s, l: lightness, alpha });
}

/**
 * Returns a color with a specified alpha value.
 * @param color The color.
 * @param alpha The new alpha value.
 * @returns The new color.
 */
export function withAlpha(color: RgbColor, alpha: number): RgbColor {
  const { r, g, b } = color;
  return { r, g, b, alpha: clamp(alpha, 0, 1) };
}

/**
 * A color palette.
 */
export interface ColorPalette {
  /**
   * The lightest shade.
   */
  readonly "50": RgbColor;

  readonly "100": RgbColor;
  readonly "200": RgbColor;
  readonly "300": RgbColor;
  readonly "400": RgbColor;
  readonly "500": RgbColor;
  readonly "600": RgbColor;
  readonly "700": RgbColor;

  /**
   * The darkest shade.
   */
  readonly "800": RgbColor;
}


/**
 * White.
 */
export const White: RgbColor = { r: 255, g: 255, b: 255, alpha: 1 };

/**
 * Black.
 */
export const Black: RgbColor = { r: 0, g: 0, b: 0, alpha: 1 };

/**
 * Transparent.
 */
export const Transparent: RgbColor = { r: 0, g: 0, b: 0, alpha: 0 };

// Provide a set of useful color palettes.

/**
 * Turquoise Colors.
 */
export const Turqoise: ColorPalette = {
  "50": fromString("#F1F6FB"),
  "100": fromString("#D8E8F6"),
  "200": fromString("#A5CAEB"),
  "300": fromString("#5FA8DC"),
  "400": fromString("#008DCE"),
  "500": fromString("#0071B3"),
  "600": fromString("#00629C"),
  "700": fromString("#005387"),
  "800": fromString("#003E6A"),
};

/**
 * Dark Blue Colors.
 */
export const DarkBlue: ColorPalette = {
  "50": fromString("#F3F5F9"),
  "100": fromString("#DDE2ED"),
  "200": fromString("#AFBDD6"),
  "300": fromString("#768EB9"),
  "400": fromString("#4868A1"),
  "500": fromString("#1E4488"),
  "600": fromString("#14316B"),
  "700": fromString("#021C4E"),
  "800": fromString("#000135"),
};

/**
 * Light Blue Colors.
 */
export const LightBlue: ColorPalette = {
  "50": fromString("#F3F4F8"),
  "100": fromString("#E1E6EF"),
  "200": fromString("#C7D2E3"),
  "300": fromString("#9FB5D1"),
  "400": fromString("#7A9BC0"),
  "500": fromString("#5B89B4"),
  "600": fromString("#44688A"),
  "700": fromString("#2D4B66"),
  "800": fromString("#193248"),
};

/**
 * Purple Colors.
 */
export const Purple: ColorPalette = {
  "50": fromString("#F6F6FB"),
  "100": fromString("#E6E6F2"),
  "200": fromString("#C7C9E2"),
  "300": fromString("#9FA4D0"),
  "400": fromString("#7F88C2"),
  "500": fromString("#5E6DB3"),
  "600": fromString("#4F5B97"),
  "700": fromString("#434D82"),
  "800": fromString("#313865"),
};

/**
 * Light Grey Colors.
 */
export const LightGrey: ColorPalette = {
  "50": fromString("#F6F8F9"),
  "100": fromString("#E9EEF1"),
  "200": fromString("#CFD9E1"),
  "300": fromString("#AEBFCC"),
  "400": fromString("#94ABBB"),
  "500": fromString("#7B98AB"),
  "600": fromString("#67899F"),
  "700": fromString("#3E5D6F"),
  "800": fromString("#2F4653"),
};

/**
 * Dark Grey Colors.
 */
export const DarkGrey: ColorPalette = {
  "50": fromString("#F1F1F1"),
  "100": fromString("#E2E1E3"),
  "200": fromString("#C3C3C7"),
  "300": fromString("#97989E"),
  "400": fromString("#60646D"),
  "500": fromString("#313E48"),
  "600": fromString("#1E2A33"),
  "700": fromString("#0C1720"),
  "800": Black,
};

/**
 * Red Colors.
 */
export const Red: ColorPalette = {
  "50": fromString("#FEF4F2"),
  "100": fromString("#FDDFDB"),
  "200": fromString("#F9B7AF"),
  "300": fromString("#EB8279"),
  "400": fromString("#DE5552"),
  "500": fromString("#CE202F"),
  "600": fromString("#B8222A"),
  "700": fromString("#A81623"),
  "800": fromString("#75010A"),
};

/**
 * Orange Colors.
 */
export const Orange: ColorPalette = {
  "50": fromString("#FFF7F4"),
  "100": fromString("#FEEAE1"),
  "200": fromString("#FCCFBB"),
  "300": fromString("#F9AF8D"),
  "400": fromString("#F79669"),
  "500": fromString("#F47E48"),
  "600": fromString("#CF6B3A"),
  "700": fromString("#AB582F"),
  "800": fromString("#85421F"),
};

/**
 * Yellow Colors.
 */
export const Yellow: ColorPalette = {
  "50": fromString("#FCFAF2"),
  "100": fromString("#F7F2E0"),
  "200": fromString("#F2E9C7"),
  "300": fromString("#EADB9E"),
  "400": fromString("#E1CF77"),
  "500": fromString("#DBC553"),
  "600": fromString("#C0A242"),
  "700": fromString("#A37F3B"),
  "800": fromString("#805F29"),
};

/**
 * Green Colors.
 */
export const Green: ColorPalette = {
  "50": fromString("#F3F7F4"),
  "100": fromString("#E1EBE6"),
  "200": fromString("#BFD7CC"),
  "300": fromString("#94C1AE"),
  "400": fromString("#72B299"),
  "500": fromString("#4AA486"),
  "600": fromString("#3D8C71"),
  "700": fromString("#317861"),
  "800": fromString("#1E5D4A"),
};
